import { isAfter } from "date-fns";

import { useEffect } from "react";

import { useRecruitmentDataQuery } from "@chef/state-management";
import { getProgressAsPercentage } from "../utils";

interface useCompetitionResultsDataProps {
  invitesRequired: number;
  deliveriesRequired: number;
  startDate: string;
  endDate: string;
  deliveryCutoffDate: string;
}

export const useCompetitionResultsData = ({
  invitesRequired,
  deliveriesRequired,
  startDate,
  endDate,
  deliveryCutoffDate,
}: useCompetitionResultsDataProps) => {
  const {
    data: recruitmentData,
    refetch,
    isLoading: isRecruitmentDataLoading,
  } = useRecruitmentDataQuery(
    {
      from: startDate,
      to: endDate,
    },
    { skip: !startDate || !endDate },
  );

  const {
    data: redeemedDiscountsDataQuery,
    isLoading: isRedeemedDiscountDataLoading,
  } = useRecruitmentDataQuery(
    {
      from: startDate,
      to: deliveryCutoffDate,
    },
    { skip: !startDate || !deliveryCutoffDate },
  );

  const isLoading = isRecruitmentDataLoading || isRedeemedDiscountDataLoading;

  const loadRecruitmentData = () => {
    refetch();
  };

  useEffect(() => {
    loadRecruitmentData();
  }, []);

  const recruitedCustomersWithDeliveries =
    redeemedDiscountsDataQuery?.recruitmentData.redeemedDiscounts || 0;

  const { recruitmentInvitations = 0 } = recruitmentData?.recruitmentData || {};

  const hasAchievedTargetInvites = recruitmentInvitations >= invitesRequired;

  const hasAchievedTargetDeliveries =
    recruitedCustomersWithDeliveries >= deliveriesRequired;

  const isCompetitionWon =
    hasAchievedTargetDeliveries && hasAchievedTargetInvites;

  const today = new Date();
  const isAfterEndDate = isAfter(today, new Date(endDate));
  const isAfterDeliveryCutoffDate = isAfter(
    today,
    new Date(deliveryCutoffDate),
  );

  const isCompetitionLost =
    (isAfterEndDate && !hasAchievedTargetInvites) ||
    (isAfterDeliveryCutoffDate && !hasAchievedTargetDeliveries);

  return {
    current: {
      currentInvitations: recruitmentInvitations,
      currentDeliveries: recruitedCustomersWithDeliveries,
    },

    progress: {
      invitesProgress: getProgressAsPercentage({
        current: recruitmentInvitations,
        target: invitesRequired,
      }),
      deliveriesProgress: getProgressAsPercentage({
        current: recruitedCustomersWithDeliveries,
        target: deliveriesRequired,
      }),
    },

    competitionResults: {
      hasAchievedTargetInvites,
      hasAchievedTargetDeliveries,
      isCompetitionWon,
      isCompetitionLost,
    },

    isRecruitmentDataLoading: isLoading,
  };
};
